/**
 * 揽收站点 2.0 表单类型（对应技术方案 §4.2 cooperation_warehouse + §五 状态机 + §九 站点信息维护）。
 * P6.4 阶段：前端 mock，后端 dubbo/GraphQL 契约就绪后对齐。供审核池建站/编辑与站点管理 v2 共用。
 */

import type { Dayjs } from 'dayjs';

/** 建联状态机 link_status */
export enum LinkStatus {
  Pending = 1, // 待上门
  PickedUpPending = 2, // 揽收成功待建联
  PickupFailedPending = 3, // 揽收失败待建联
  Linked = 4, // 已建联
  Unreachable = 5, // 确认无法建联
  OnsiteInProgress = 6, // 上门中（派单给司机后；仅创建预约单不流转，取消预约单回退待上门）
}

/** 仓库类型 */
export enum WarehouseType {
  Commercial = 1, // 商业仓
  Home = 2, // 家庭仓
  Customs = 3, // 清关行
}

/** 联系人 */
export interface SiteContactPerson {
  name?: string;
  phoneCountryCode?: string;
  phone?: string;
  /** 分机 */
  extension?: string;
  inBlacklist?: boolean;
}

/** 长期揽收排期行（standing_schedule_json 一行） */
export interface StandingScheduleRow {
  weekdays?: number[]; // 1~7
  /**
   * HH:mm。**表单内运行时是 dayjs**（TimePicker 的值），从服务端读入或提交时才是字符串——
   * PickupSiteFormDrawer 的 timeStr() 两种都兼容。类型如实反映两态，避免调用方误以为恒为 string。
   */
  startTime?: string | Dayjs;
  endTime?: string | Dayjs;
  /**
   * 营业时间（2.2 需求点3）——仓库几点开门，与上面的**期望上门时间**是两回事：
   * 仓库 08:00-20:00 开门，但客户希望司机 09:00-12:00 来。
   *
   * 2.2 之前两者共用 startTime/endTime 一组值，本次拆开。
   * **建单链路仍只取期望上门时间**（后端 PickupScheduleResolver 不读这两个字段），拆分对建单零回归。
   *
   * 存量行无这两个键：回显时按「原值即两者共用」回填成 startTime/endTime，
   * 等于 PRD「历史数据：营业时间 = 原有的工作时间自动填充」的惰性迁移版 —— 运营编辑一次即落库。
   */
  businessStartTime?: string | Dayjs;
  businessEndTime?: string | Dayjs;
  /**
   * 车型，口径为 vehicle_type（与站点级 vehicleTypeId 同一 id 空间）。
   * 历史 standing_schedule_json 里存的是 vehicle_model id，与本字段不是同一 id 空间
   * （实测同一站点 model=4 / type=6），无法安全映射，故旧行读入后车型留空由运营重选。
   */
  vehicleTypeId?: number;
}

/** 黑名单配置行（独立于联系人；对应 cooperation_warehouse_blacklist，表仅 name/phone/status，国家码/分机暂无列 todo wuhao） */
export interface BlacklistContactRow {
  phoneCountryCode?: string;
  phone?: string;
  extension?: string;
}

/** AB 地址映射行（错误地址 A → 实际站点；地址/联系人/联系方式/客户渠道） */
export interface AbMappingRow {
  /** A 地址分段输入（与主揽收地址同一组件）：标准化后回填下方四段。原单串 fullAddress 已废弃 */
  addrFields?: PickupSiteFormValues['addrFields'];
  // A 地址（错误下单地址）结构化四段（#6/G6：标准化按钮预览回填，保存时后端权威标准化算 coreHash）
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  contactName?: string;
  contactPhone?: string;
  channelId?: number;
  standardized?: boolean;
}

/** 站点关联地址（对齐生产 1.0：读 cooperation_warehouse.related_address_json 的 address1/address2 对，只读展示） */
export interface SiteRelateAddress {
  address1?: string;
  address2?: string;
}

export interface PickupSiteFormValues {
  id?: number;
  name?: string;
  linkStatus?: LinkStatus;
  warehouseType?: WarehouseType;
  brand?: string;
  subBrand?: string;
  /**
   * 其他备注（基础信息区）→ 落 `cooperation_warehouse.other_remark`，与「备注信息」(remark) 是两个独立列。
   *
   * <p>此前这里写的是"后端暂并入 remark，第二备注字段待确认 TODO#5"，**已过期**：DB 列与
   * api-server 全链路（Exposed 表定义 / insert / update / read / GraphQL input+type）都已支持，
   * buildSaveData 也一直在发它。2.2 需求点1 的「异常地址—$原因」默认填充就落在本字段。
   */
  otherRemark?: string;
  /**
   * 分段揽收地址（街道 / Dock / 州 / 城市 / 邮编 + 已标准化标记 + 标准化结果）。
   * 单文本框方案（原 pickupAddress）已废弃：无论前端按逗号拆还是后端 parseRawAddress 拆都在猜分段，
   * 街道自带逗号即拆错。类型见 components/PickupAddressFields。
   */
  addrFields?: {
    street?: string;
    unit?: string;
    dock?: string;
    state?: string;
    city?: string;
    zipcode?: string;
    standardized?: boolean;
    resolved?: {
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      state?: string;
      zipcode?: string;
      country?: string;
      standardized?: boolean;
    };
  };
  // 结构化揽收地址（#2：标准化按钮需 street/city/state/zip 分段入参；保存也读这四段）
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  dock?: string;
  contactPersons: SiteContactPerson[];
  groupId?: string;
  groupName?: string;
  standingPickup?: boolean;
  standingClientId?: number; // 长期揽收预约客户（单选）→ standingPickupChannelId
  relatedClientIds?: number[]; // 关联客户（多选）→ relatedChannelIds
  workWeekdays?: number[]; // 工作日（站点营业日，多选 1~7）→ workWeekdayList；揽收周期只能在其范围内选
  standingSchedules?: StandingScheduleRow[];
  expectedTimeStart?: string; // 非长期：期望上门时间
  expectedTimeEnd?: string;
  /** 非长期：车型。口径为 vehicle_type（vehicle_model 已废弃），选项取 useVehicleTypeOptions */
  vehicleTypeId?: number;
  /**
   * 站点原有的 use_vehicle_model_id，仅透传回存，不在表单展示。
   * 该列已废弃但仍被预约单链路读取（PickupAppointmentApplicationImpl 复制到预约单 → 派车），
   * 保存时若不带原值会被清空而影响派车，故原样回写，待下游切到 vehicleTypeId 后再废弃。
   */
  useVehicleModelId?: number;
  /**
   * 站点原有的 channel_id，仅透传回存，不在表单展示。
   * 它是 1.0 表单「长期揽收 / 预约客户」的落库列，长期揽收预约单调度器
   * （pickup-service StandingPickupBackfillJobService）仍按 `channel_id is null` 硬过滤跳过站点。
   * 2.0 已把该概念迁到 standingClientId(standing_pickup_channel_id)，但调度器未切，
   * 故长期揽收时以 standingClientId 覆盖、其余情况原样回写，避免编辑站点后预约单停止生成。
   */
  channelId?: number;
  /** 站点原有的 email，仅透传回存：预约单 contactEmail 取它 */
  email?: string;
  /** 站点原有的建联群二维码 key，仅透传回存，不在本表单展示 */
  groupQrcodeS3Key?: string;
  /**
   * 站点原有的两个命中键，仅透传。
   * 「确认无法建联(link_status=5)」站点编辑时走 skipAddressStandardize（地址标准化不了、按原样保存），
   * 该路径要求入参自带 hash——后端不兜底计算，而 save() 是全列覆盖，不传就把站点命中键写成 NULL。
   */
  addressCoreHash?: string;
  addressAnalysisHash?: string;
  remark?: string;
  blacklistContacts?: BlacklistContactRow[];
  abMappings?: AbMappingRow[];
  /**
   * 站点别名（alias_json）。
   *
   * **必须由本表单采集并回传**：后端 `saveCooperationWarehouse` 对 alias_json 是
   * `it[aliasJson] = aliasList?.let { toJson(it) }` —— **无条件写**，不发即写 NULL。
   * 紧邻的 relatedAddressJson 是条件写（`?.let { it[...] = }`），两者写法不一致，
   * 别名这条会在保存时静默清空。本表单取代老抽屉成为灰度 hub 的站点编辑入口后，
   * 不带这个字段就等于「运营改个电话，别名没了」。
   */
  aliasList?: string[];
  /**
   * 站点经纬度，仅透传回存，本表单不展示也不编辑（产品已决定 2.2 不再手填经纬度）。
   *
   * **与 aliasList 同一类问题**：后端 `saveCooperationWarehouse` 对 latitude/longitude 也是
   * 无条件写（`it[latitude] = cooperationWarehouse.latitude`），不发即写 NULL。老 1.0 抽屉
   * 一直在回传，本表单取代它成为灰度 hub 的编辑入口后不带，就等于「运营改个电话，坐标没了」。
   * 下游在读：预约单详情的 cooperationWarehouseLatitude、POP 详情 siteLatitude、路线规划。
   *
   * ⚠️ 取值口径沿用老表单：GraphQL 的 latitude 字段在走新地址库的 hub 上返回的是
   * `address_core_latitude`（见 `toCooperationWarehouseType(useAddressLibrary)`），
   * 回写即把地址库坐标落进 latitude 列。这不是本次引入的口径，与 1.0 表单逐字节一致，
   * 且严格优于现状（现状是写 NULL）。
   */
  latitude?: number;
  longitude?: number;
  /** 关联地址（relate 表，只读展示） */
  relateAddresses?: SiteRelateAddress[];
}

export type PickupSiteFormMode = 'create' | 'edit' | 'view';
